/**
 * Snowflake ID branded type.
 *
 * Backend (`main-api/app/shared/types/snowflake_id.py`) emits all Snowflake
 * IDs as JSON strings (>=17 digits — exceeds JS Number.MAX_SAFE_INTEGER ~=
 * 9 * 10^15).
 *
 * The brand is compile-time only — at runtime this is just `string`. The
 * brand prevents accidental `Number(snowflakeId)` / `parseInt(...)` /
 * `+snowflakeId` conversions, which lose precision and produce 404s.
 *
 * Use `SnowflakeId` for any field that maps to a database PK or FK in any
 * backend model. Use plain `string` for hashes, codes, titles, etc.
 */
export type SnowflakeId = string & { readonly __snowflakeBrand: unique symbol };

/** Nullable convenience — common in nullable FK fields (e.g. `productId`). */
export type SnowflakeIdOrNull = SnowflakeId | null;

/**
 * Cast a string to `SnowflakeId` without runtime validation.
 *
 * Use at API boundaries where the runtime guarantees the value is a Snowflake
 * ID (e.g. directly from a backend response that already passed
 * `safeJsonParse`).
 */
export function asSnowflakeId(value: string): SnowflakeId {
  return value as SnowflakeId;
}

/**
 * Validating constructor: throws if `value` is not a non-empty digit string.
 * Use when accepting input from untrusted sources (URL params, user input).
 */
export function toSnowflakeId(value: unknown): SnowflakeId {
  if (typeof value !== 'string' || !/^\d+$/.test(value)) {
    throw new TypeError(
      `toSnowflakeId: expected non-empty digit string, got ${typeof value}: ${String(value)}`,
    );
  }
  return value as SnowflakeId;
}

/** Same as `toSnowflakeId` but returns `null` instead of throwing. */
export function tryToSnowflakeId(value: unknown): SnowflakeId | null {
  if (typeof value !== 'string' || !/^\d+$/.test(value)) return null;
  return value as SnowflakeId;
}

/** Type guard. */
export function isSnowflakeId(value: unknown): value is SnowflakeId {
  return typeof value === 'string' && /^\d+$/.test(value);
}
